/* eslint-env browser */
// The legal pages (Terms and conditions, Privacy policy; owner, 5 Nov 2026: "can you also
// update /term-condition and /privacy-policy") in About us's look (SitePage): the night
// hero with the page's name, then the owner's text from /languages, unchanged, in a
// reading column beside an "On this page" list that follows the reader on wide screens
// (folded away above the text on phones), and a closing card with the contact email and
// the Contact us page. Headings show without a trailing colon, and all-capitals Latin
// ones in sentence case: the same words, in today's style.
import React, { useEffect, useRef, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Container, Typography, Button, Grid } from '@mui/material';
import { ArrowForward, ExpandMore } from '@mui/icons-material';
import SitePage, { NightHero, useSiteWords, textLinkSx } from './SitePage';
import { tokens as c } from '../../theme';

// "Acceptance of Terms :" -> "Acceptance of Terms"; "LOG DATA" -> "Log data".
export function legalHeading(text) {
  let h = String(text || '').replace(/[\s:;፡።፣፤፥፦]+$/, '');
  if (/[A-Z]/.test(h) && !/[a-z]/.test(h)) h = h.charAt(0) + h.slice(1).toLowerCase();
  return h;
}

// The reading column's type: paragraphs and bullet lists.
const textSx = { fontSize: { xs: 17, md: 18 }, lineHeight: 1.75, color: c.INK_2, overflowWrap: 'anywhere', textWrap: 'pretty' };

export function LegalP({ children }) {
  return (
    <Typography dir="auto" sx={{ ...textSx, mt: 2 }}>
      {children}
    </Typography>
  );
}

export function LegalList({ items }) {
  return (
    <Box component="ul" dir="auto" sx={{ ...textSx, mt: 2, mb: 0, pl: 3.5, '& li': { mt: 1 }, '& li::marker': { color: c.ACCENT } }}>
      {items.map((text, i) => (
        <li key={i}>{text}</li>
      ))}
    </Box>
  );
}

// sections: [{ id, heading, body }]; closing: { id, heading, text, email } or none.
export default function LegalPage({ mainId, heroId, icon, title, sub, intro, sections, closing, ...rest }) {
  const { t, L, track } = useSiteWords();
  const toc = sections.map((s) => ({ id: s.id, heading: s.heading })).concat(closing ? [{ id: closing.id, heading: closing.heading }] : []);
  const tocKey = toc.map((x) => x.id).join('|');
  const [active, setActive] = useState(toc.length ? toc[0].id : null);
  // A click on the list shows that section as read, though a short one near the end
  // cannot scroll up to the line: the jump's own scroll does not overrule it.
  const clickedUntil = useRef(0);

  // The section being read, for the list: the last heading above a line just below where a
  // jump puts a heading (160 px of scroll padding, under the fixed header); the first one at
  // the top of the page, the last one at its very end. Worked out on scroll (once a frame),
  // so a jump to the top or a link to a section updates it too.
  useEffect(() => {
    const ids = tocKey.split('|').filter(Boolean);
    if (!ids.length) return undefined;
    let frame = 0;
    const update = () => {
      frame = 0;
      if (Date.now() < clickedUntil.current) return;
      const line = 200;
      const atEnd = window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 2;
      let current = ids[0];
      ids.forEach((id) => {
        const el = document.getElementById(id);
        if (el && el.getBoundingClientRect().top <= line) current = id;
      });
      setActive(atEnd ? ids[ids.length - 1] : current);
    };
    const onScroll = () => {
      if (!frame) frame = window.requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
    };
  }, [tocKey]);

  const tocTitle = L('lg_on_this_page', 'On this page');
  const tocList = (
    <Box component="ol" sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gap: 0.25 }}>
      {toc.map((x) => {
        const on = active === x.id;
        return (
          <li key={x.id}>
            <Box
              component="a"
              href={`#${x.id}`}
              aria-current={on ? 'location' : undefined}
              onClick={() => {
                clickedUntil.current = Date.now() + 700;
                setActive(x.id);
              }}
              dir="auto"
              sx={{
                display: 'block',
                py: 0.875,
                pl: 1.75,
                pr: 1,
                borderLeft: `2px solid ${on ? c.ACCENT : c.OUTLINE}`,
                color: on ? c.ACCENT_TEXT : c.INK_2,
                fontWeight: on ? 700 : 500,
                fontSize: 15,
                lineHeight: 1.45,
                textDecoration: 'none',
                '&:hover': { color: c.ACCENT_TEXT },
                '&:focus-visible': { outline: `3px solid ${c.ACCENT_TEXT}`, outlineOffset: 2, borderRadius: '4px' },
              }}
            >
              {x.heading}
            </Box>
          </li>
        );
      })}
    </Box>
  );

  return (
    <SitePage mainId={mainId} {...rest}>
      <NightHero id={heroId} icon={icon} eyebrow={L('lg_eyebrow', 'Legal')} title={title} sub={sub} pb={{ xs: 9, md: 12 }} />
      <Container maxWidth="lg" sx={{ py: { xs: 5, md: 10 } }}>
        <Grid container spacing={{ xs: 3, md: 8 }}>
          <Grid item xs={12} md={4} lg={3}>
            {/* Phones: the list folded away above the text. */}
            <Box
              component="details"
              sx={{
                display: { xs: 'block', md: 'none' },
                px: 2.5,
                py: 1.75,
                borderRadius: '16px',
                border: `1px solid ${c.OUTLINE}`,
                bgcolor: c.SCREEN_BG,
                '&[open] .lg-chevron': { transform: 'rotate(180deg)' },
              }}
            >
              <Box
                component="summary"
                sx={{
                  cursor: 'pointer',
                  listStyle: 'none',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  minHeight: 32,
                  fontWeight: 700,
                  fontSize: 16,
                  color: c.INK,
                  '&::-webkit-details-marker': { display: 'none' },
                  '&:focus-visible': { outline: `3px solid ${c.ACCENT_TEXT}`, outlineOffset: 4, borderRadius: '6px' },
                }}
              >
                {tocTitle}
                <ExpandMore className="lg-chevron" aria-hidden sx={{ color: c.INK_2, transition: 'transform 160ms ease-out' }} />
              </Box>
              <Box component="nav" aria-label={tocTitle} sx={{ mt: 1.5 }}>
                {tocList}
              </Box>
            </Box>
            {/* Wide screens: the list follows the reader. */}
            <Box component="nav" aria-label={tocTitle} sx={{ display: { xs: 'none', md: 'block' }, position: 'sticky', top: 120 }}>
              <Typography sx={{ color: c.ACCENT_TEXT, fontWeight: 700, fontSize: 13, letterSpacing: track('0.14em'), textTransform: 'uppercase', mb: 1.5 }}>
                {tocTitle}
              </Typography>
              {tocList}
            </Box>
          </Grid>
          <Grid item xs={12} md={8} lg={9}>
            <Box sx={{ maxWidth: 760 }}>
              {intro}
              {sections.map((s) => (
                <Box component="section" key={s.id} aria-labelledby={s.id} sx={{ mt: { xs: 5, md: 6 } }}>
                  <Typography component="h2" id={s.id} dir="auto" sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 700, color: c.INK, lineHeight: 1.3, textWrap: 'balance' }}>
                    {s.heading}
                  </Typography>
                  {s.body}
                </Box>
              ))}
              {closing ? (
                <Box
                  component="section"
                  aria-labelledby={closing.id}
                  sx={{ mt: { xs: 6, md: 8 }, p: { xs: 3, md: 4 }, borderRadius: '24px', bgcolor: c.ACCENT_TINT, border: `1px solid ${c.OUTLINE}` }}
                >
                  <Typography component="h2" id={closing.id} dir="auto" sx={{ fontSize: { xs: 22, md: 26 }, fontWeight: 700, color: c.INK, lineHeight: 1.3 }}>
                    {closing.heading}
                  </Typography>
                  <Typography dir="auto" sx={{ ...textSx, mt: 1.5 }}>
                    {closing.text}
                    {closing.email ? (
                      <>
                        {' '}
                        <Box component="a" href={`mailto:${closing.email}`} sx={textLinkSx}>
                          {closing.email}
                        </Box>
                      </>
                    ) : null}
                  </Typography>
                  <Button
                    component={RouterLink}
                    to="/contact-us"
                    endIcon={<ArrowForward />}
                    sx={{
                      mt: 3,
                      minHeight: 48,
                      px: 2.5,
                      borderRadius: '12px',
                      color: c.ACCENT_TEXT,
                      bgcolor: c.WHITE,
                      fontSize: 16,
                      fontWeight: 700,
                      textTransform: 'none',
                      border: `1.5px solid ${c.ACCENT_TEXT}`,
                      '&:hover': { bgcolor: c.WHITE, borderColor: c.BUTTON_PRIMARY_PRESSED, color: c.BUTTON_PRIMARY_PRESSED },
                      '&:focus-visible': { outline: `3px solid ${c.ACCENT_TEXT}`, outlineOffset: 3 },
                    }}
                  >
                    {t('contact_us')}
                  </Button>
                </Box>
              ) : null}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </SitePage>
  );
}
